import { useState, useMemo } from 'react'
import { useMetaData } from '../queries/useMetaData.js'
import { useGoalsData } from '../queries/useGoalsData.js'
import {
  useSaveExecSummary,
  useGenerateExecSummary,
} from '../mutations/useExecSummary.js'
import { useAiPreferenceOp } from '../mutations/useAiPreferences.js'
import { useUIStore } from '../store/uiStore.js'
import { timeAgo } from '../data/metaKeys.js'
import RichText from './RichText.jsx'
import FollowUpsSection from './FollowUpsSection.jsx'

/**
 * ExecutiveSummaryCard — top-of-dashboard AI-generated summary.
 *
 * Reads meta.execSummary (a single slot) and meta.aiPreferences (a
 * growing list of user-provided style/tone guides). Provides three
 * primary actions:
 *   • Generate — calls the worker's /ai-summary with all current weekly
 *     summaries + AI preferences, returns { tldr, flagsSummary }, writes
 *     into the cache but does NOT auto-save until the user accepts.
 *   • Save — persists the edited TLDR/flags to meta.execSummary.
 *   • Edit AI preferences — add/remove writing-style rules that get
 *     included as context on every future generation.
 *
 * All three mutations use dedicated partial-update endpoints
 * (/meta/execSummary, /meta/aiPreferences, /ai-summary), so they're
 * immune to the full-meta POST resurrection hazard.
 */
export default function ExecutiveSummaryCard() {
  const { data: meta } = useMetaData()
  const { data: goalsData } = useGoalsData()
  const currentUser = useUIStore((s) => s.currentUser) || 'Unknown'

  const [expanded, setExpanded] = useState(true)
  const [editingPrefs, setEditingPrefs] = useState(false)
  const [draftTldr, setDraftTldr] = useState(null) // null = not editing
  const [draftWins, setDraftWins] = useState(null)
  const [draftFlags, setDraftFlags] = useState(null)
  const [newPrefText, setNewPrefText] = useState('')
  const [pendingPreview, setPendingPreview] = useState(null) // { tldr, winsSummary, flagsSummary }

  const saveExec = useSaveExecSummary()
  const generateExec = useGenerateExecSummary()
  const prefOp = useAiPreferenceOp()

  const openArchive = useUIStore((s) => s.openArchive)
  const exec = meta.execSummary || null
  const prefs = meta.aiPreferences || []
  const weeklySummariesMap = meta.weeklySummary || {}
  const weeklyHistory = meta.weeklyHistory || {}
  const archiveCount = Object.keys(weeklyHistory).length

  // Walk EVERY goal in goalsData (skipping section dividers) so the AI
  // has a complete picture of what exists — including goals that nobody
  // has filled in yet. The prompt tells Claude to flag missing updates
  // as a data gap rather than silently ignore them. This stops the AI
  // from producing a rosy summary that omits half the company's work
  // just because those goals have empty weekly summaries.
  const weeklySummariesForAi = useMemo(() => {
    if (!Array.isArray(goalsData)) return []
    const out = []
    for (let pi = 0; pi < goalsData.length; pi++) {
      const pillar = goalsData[pi]
      if (!pillar?.goals) continue
      for (let gi = 0; gi < pillar.goals.length; gi++) {
        const goal = pillar.goals[gi]
        if (!goal || goal.type === 'section') continue
        const key = `p${pi}-g${gi}`
        const value = weeklySummariesMap[key] || {}
        const hasUpdate = Boolean(value.tldr || value.wins || value.flags)
        out.push({
          goalName: `${pillar.pillar} › ${goal.goal || 'Untitled goal'}`,
          dri: goal.dri || null,
          hasUpdate,
          tldr: value.tldr || '',
          wins: value.wins || '',
          flags: value.flags || '',
        })
      }
    }
    return out
  }, [weeklySummariesMap, goalsData])
  const goalsWithUpdates = weeklySummariesForAi.filter((g) => g.hasUpdate).length
  const totalGoals = weeklySummariesForAi.length

  // Tertiary context for the AI: all currently-open action items. The
  // worker prompt is told NOT to list these individually — they live in
  // the Follow-ups section below — but to mention them if any are
  // overdue or blocking a goal-level flag.
  const openActionItemsForAi = useMemo(() => {
    if (!Array.isArray(goalsData)) return []
    const today = new Date().toISOString().slice(0, 10)
    const actionItemsMap = meta?.actionItems || {}
    const out = []
    for (const [key, items] of Object.entries(actionItemsMap)) {
      if (!Array.isArray(items)) continue
      const match = /^p(\d+)-g(\d+)$/.exec(key)
      if (!match) continue
      const pi = Number(match[1])
      const gi = Number(match[2])
      const pillar = goalsData[pi]
      const goal = pillar?.goals?.[gi]
      if (!goal) continue
      for (const item of items) {
        if (item.status === 'done') continue
        out.push({
          pillar: pillar.pillar,
          goalName: goal.goal || 'Untitled goal',
          task: item.task,
          owner: item.owner && item.owner !== '—' ? item.owner : null,
          dueDate: item.dueDate || null,
          overdue: Boolean(item.dueDate && item.dueDate < today),
        })
      }
    }
    return out
  }, [meta?.actionItems, goalsData])

  // Secondary context: flatten every subgoal with EITHER a progress number
  // or a free-text update. Empty subgoals are skipped so the prompt isn't
  // padded with noise.
  const subgoalUpdatesForAi = useMemo(() => {
    if (!Array.isArray(goalsData)) return []
    const out = []
    for (const pillar of goalsData) {
      if (!pillar?.goals) continue
      for (const goal of pillar.goals) {
        if (!Array.isArray(goal?.subGoals)) continue
        for (const sub of goal.subGoals) {
          const hasProgress =
            sub.current !== undefined &&
            sub.current !== null &&
            sub.current !== ''
          const hasUpdate = !!(sub.update && String(sub.update).trim())
          if (!hasProgress && !hasUpdate) continue
          out.push({
            pillar: pillar.pillar,
            goalName: goal.name || 'Untitled goal',
            subgoalName: sub.subgoal || 'Untitled sub-goal',
            current: sub.current,
            target: sub.target,
            unit: sub.unit || '',
            update: sub.update || '',
          })
        }
      }
    }
    return out
  }, [goalsData])

  const handleGenerate = async () => {
    try {
      const result = await generateExec.mutateAsync({
        weeklySummaries: weeklySummariesForAi,
        subgoalUpdates: subgoalUpdatesForAi,
        openActionItems: openActionItemsForAi,
        aiPreferences: prefs,
      })
      setPendingPreview(result)
      setDraftTldr(result.tldr || '')
      setDraftWins(result.winsSummary || '')
      setDraftFlags(result.flagsSummary || '')
    } catch (err) {
      console.error('[exec-summary] generate failed', err)
      alert('Couldn\'t generate summary — check the worker logs for details.')
    }
  }

  const handleSave = () => {
    saveExec.mutate({
      tldr: draftTldr ?? (exec?.tldr || ''),
      winsSummary: draftWins ?? (exec?.winsSummary || ''),
      flagsSummary: draftFlags ?? (exec?.flagsSummary || ''),
      generatedAt: pendingPreview ? new Date().toISOString() : exec?.generatedAt,
      generatedBy: pendingPreview ? currentUser : exec?.generatedBy,
      edited: !pendingPreview, // if we came from a preview, it's freshly generated, not edited
    })
    setPendingPreview(null)
    setDraftTldr(null)
    setDraftWins(null)
    setDraftFlags(null)
  }

  const handleCancelEdit = () => {
    setPendingPreview(null)
    setDraftTldr(null)
    setDraftWins(null)
    setDraftFlags(null)
  }

  const handleClear = () => {
    if (!confirm('Clear the current executive summary?')) return
    saveExec.mutate({ clear: true })
    setPendingPreview(null)
    setDraftTldr(null)
    setDraftWins(null)
    setDraftFlags(null)
  }

  const handleAddPref = () => {
    const text = newPrefText.trim()
    if (!text) return
    const pref = {
      id: Date.now().toString(36) + Math.random().toString(36).slice(2, 5),
      text,
      addedAt: new Date().toISOString(),
      addedBy: currentUser,
    }
    prefOp.mutate({
      op: 'create',
      pref,
      optimistic: (list) => [...list, pref],
    })
    setNewPrefText('')
  }

  const handleRemovePref = (id) => {
    prefOp.mutate({
      op: 'remove',
      id,
      optimistic: (list) => list.filter((p) => p.id !== id),
    })
  }

  const isEditing =
    draftTldr !== null || draftWins !== null || draftFlags !== null
  const showGenerated = isEditing || !!exec
  const generating = generateExec.isPending

  return (
    <section
      data-tour="exec-summary"
      style={{
        margin: '0 0 16px',
        background: '#fff',
        border: '1px solid var(--border)',
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <header
        onClick={() => setExpanded((e) => !e)}
        style={{
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          background: 'linear-gradient(135deg, #eff6ff 0%, #f5f3ff 100%)',
          borderBottom: expanded ? '1px solid var(--border)' : 'none',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <span style={{ fontSize: 18 }}>✨</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--text)' }}>
            Executive Summary
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
            {exec?.generatedAt ? (
              <>
                {exec.edited ? 'Edited' : 'Generated'}{' '}
                {timeAgo(new Date(exec.generatedAt).getTime())}
                {exec.generatedBy ? ` by ${exec.generatedBy}` : ''}
              </>
            ) : (
              'No summary yet — generate one from this week\'s updates'
            )}
          </div>
        </div>
        {archiveCount > 0 && (
          <button
            data-tour="archive-button"
            onClick={(e) => {
              e.stopPropagation()
              openArchive('latest')
            }}
            title="View past weekly snapshots"
            style={{
              padding: '4px 10px',
              border: '1px solid var(--border)',
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--muted)',
              background: '#fff',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            📅 Past weeks ({archiveCount})
          </button>
        )}
        <span
          style={{
            fontSize: 12,
            color: 'var(--muted)',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.15s',
          }}
        >
          ▾
        </span>
      </header>

      {expanded && (
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Body */}
          {!showGenerated ? (
            <EmptyState
              onGenerate={handleGenerate}
              generating={generating}
              goalsWithUpdates={goalsWithUpdates}
              totalGoals={totalGoals}
            />
          ) : (
            <SummaryBody
              tldr={isEditing ? draftTldr : exec?.tldr}
              wins={isEditing ? draftWins : exec?.winsSummary}
              flags={isEditing ? draftFlags : exec?.flagsSummary}
              editing={isEditing}
              onEditTldr={() => setDraftTldr(exec?.tldr || '')}
              onEditWins={() => setDraftWins(exec?.winsSummary || '')}
              onEditFlags={() => setDraftFlags(exec?.flagsSummary || '')}
              onChangeTldr={(v) => setDraftTldr(v)}
              onChangeWins={(v) => setDraftWins(v)}
              onChangeFlags={(v) => setDraftFlags(v)}
            />
          )}

          {/* Actions row */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
              borderTop: '1px solid var(--border)',
              paddingTop: 12,
            }}
          >
            {isEditing ? (
              <>
                <button
                  onClick={handleSave}
                  disabled={saveExec.isPending}
                  style={primaryBtn}
                >
                  {saveExec.isPending ? 'Saving…' : pendingPreview ? 'Save as current' : 'Save edits'}
                </button>
                <button onClick={handleCancelEdit} style={secondaryBtn}>
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleGenerate}
                  disabled={generating || goalsWithUpdates === 0}
                  style={primaryBtn}
                  title={
                    goalsWithUpdates === 0
                      ? 'Add weekly TLDR/Wins/Flags to at least one goal first'
                      : 'Ask Claude for a fresh summary based on all goals (updated and not)'
                  }
                >
                  {generating ? 'Generating…' : exec ? 'Regenerate' : 'Generate with AI'}
                </button>
                {exec && (
                  <button
                    onClick={() => {
                      setDraftTldr(exec.tldr || '')
                      setDraftWins(exec.winsSummary || '')
                      setDraftFlags(exec.flagsSummary || '')
                    }}
                    style={secondaryBtn}
                  >
                    Edit
                  </button>
                )}
                {exec && (
                  <button onClick={handleClear} style={dangerBtn}>
                    Clear
                  </button>
                )}
              </>
            )}
            <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--muted-light)' }}>
              {totalGoals > 0
                ? `${goalsWithUpdates}/${totalGoals} goal${totalGoals === 1 ? '' : 's'} have weekly updates${
                    subgoalUpdatesForAi.length > 0
                      ? ` · ${subgoalUpdatesForAi.length} sub-goal note${subgoalUpdatesForAi.length === 1 ? '' : 's'}`
                      : ''
                  }`
                : 'No goals yet'}
            </span>
          </div>

          {/* Follow-ups — deterministic list of open action items.
              data-tour anchor lives here (not inside FollowUpsSection)
              so the tour can find it even when the section is empty. */}
          <div
            data-tour="follow-ups"
            style={{
              borderTop: '1px solid var(--border)',
              paddingTop: 12,
            }}
          >
            <FollowUpsSection />
          </div>

          {/* AI preferences */}
          <div style={{ marginTop: 4 }}>
            <button
              onClick={() => setEditingPrefs((e) => !e)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: 11,
                fontWeight: 700,
                color: 'var(--muted)',
                cursor: 'pointer',
                padding: 0,
                textTransform: 'uppercase',
                letterSpacing: 0.06,
              }}
            >
              {editingPrefs ? '▾' : '▸'} Writing preferences ({prefs.length})
            </button>
            {editingPrefs && (
              <PreferencesEditor
                prefs={prefs}
                newText={newPrefText}
                setNewText={setNewPrefText}
                onAdd={handleAddPref}
                onRemove={handleRemovePref}
                saving={prefOp.isPending}
              />
            )}
          </div>
        </div>
      )}
    </section>
  )
}

/* ─── Sub-components ──────────────────────────────────────────────── */

function EmptyState({ onGenerate, generating, goalsWithUpdates, totalGoals }) {
  return (
    <div
      style={{
        padding: '18px 16px',
        background: '#fafafa',
        border: '1px dashed var(--border)',
        borderRadius: 8,
        textAlign: 'center',
      }}
    >
      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
        {goalsWithUpdates === 0 ? (
          <>
            No weekly updates yet — fill in TLDR / Wins / Flags on at least one
            goal, then generate a summary.
          </>
        ) : (
          <>
            Generate a consolidated TLDR, Wins, and Flags across all{' '}
            {totalGoals} Q2 goals.
          </>
        )}
      </div>
      <button
        onClick={onGenerate}
        disabled={generating || goalsWithUpdates === 0}
        style={{
          ...primaryBtn,
          opacity: goalsWithUpdates === 0 ? 0.5 : 1,
        }}
      >
        {generating ? 'Generating…' : '✨ Generate with AI'}
      </button>
    </div>
  )
}

function SummaryBody({
  tldr,
  wins,
  flags,
  editing,
  onEditTldr,
  onEditWins,
  onEditFlags,
  onChangeTldr,
  onChangeWins,
  onChangeFlags,
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Block
        label="TLDR"
        value={tldr}
        editing={editing}
        onStartEdit={onEditTldr}
        onChange={onChangeTldr}
        placeholder="A 2–4 sentence summary of where Q2 stands overall."
      />
      <Block
        label="Wins"
        value={wins}
        editing={editing}
        onStartEdit={onEditWins}
        onChange={onChangeWins}
        placeholder="The most important progress across goals this week."
        accent="#166534"
      />
      <Block
        label="Flags & Risks"
        value={flags}
        editing={editing}
        onStartEdit={onEditFlags}
        onChange={onChangeFlags}
        placeholder="Blockers, risks, and the biggest things leadership should know."
        accent="#b91c1c"
      />
    </div>
  )
}

function Block({ label, value, editing, onStartEdit, onChange, placeholder, accent }) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 800,
          color: accent || 'var(--muted)',
          textTransform: 'uppercase',
          letterSpacing: 0.08,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      {editing ? (
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={{
            width: '100%',
            minHeight: 60,
            padding: '8px 10px',
            border: '1px solid var(--border)',
            borderRadius: 6,
            fontSize: 13,
            fontFamily: 'inherit',
            lineHeight: 1.5,
            resize: 'vertical',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      ) : value ? (
        <div
          onClick={onStartEdit}
          style={{
            fontSize: 13,
            color: 'var(--text)',
            lineHeight: 1.55,
            whiteSpace: 'pre-wrap',
            cursor: 'text',
            padding: '4px 0',
          }}
          title="Click to edit"
        >
          <RichText text={value} />
        </div>
      ) : (
        <div
          onClick={onStartEdit}
          style={{
            fontSize: 12,
            color: 'var(--muted-light)',
            fontStyle: 'italic',
            cursor: 'text',
            padding: '4px 0',
          }}
        >
          {placeholder}
        </div>
      )}
    </div>
  )
}

function PreferencesEditor({ prefs, newText, setNewText, onAdd, onRemove, saving }) {
  return (
    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.5 }}>
        Each preference is included with every AI summary request.
        Good examples: <em>"Plain language, no buzzwords"</em>,{' '}
        <em>"Call out Trust Enforcer progress explicitly"</em>,{' '}
        <em>"Keep TLDR under 3 sentences"</em>.
      </div>
      {prefs.length > 0 && (
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          {prefs.map((p) => (
            <li
              key={p.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 8px',
                background: '#fafafa',
                border: '1px solid var(--border)',
                borderRadius: 6,
                fontSize: 12,
              }}
            >
              <span style={{ flex: 1, color: 'var(--text-secondary)' }}>{p.text}</span>
              <button
                onClick={() => onRemove(p.id)}
                title="Remove"
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--muted-light)',
                  fontSize: 14,
                  cursor: 'pointer',
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          type="text"
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              onAdd()
            }
          }}
          placeholder="Add a writing-style preference…"
          style={{
            flex: 1,
            padding: '6px 10px',
            border: '1px solid var(--border)',
            borderRadius: 6,
            fontSize: 12,
            fontFamily: 'inherit',
            outline: 'none',
          }}
        />
        <button
          onClick={onAdd}
          disabled={!newText.trim() || saving}
          style={{
            ...primaryBtn,
            padding: '6px 12px',
            fontSize: 11,
          }}
        >
          Add
        </button>
      </div>
    </div>
  )
}

/* ─── Shared button styles ────────────────────────────────────────── */

const primaryBtn = {
  padding: '6px 14px',
  border: 'none',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 700,
  background: 'var(--accent)',
  color: '#fff',
  cursor: 'pointer',
}

const secondaryBtn = {
  padding: '6px 14px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600,
  background: '#fff',
  color: 'var(--text-secondary)',
  cursor: 'pointer',
}

const dangerBtn = {
  padding: '6px 14px',
  border: '1px solid #fecaca',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600,
  background: '#fff',
  color: '#b91c1c',
  cursor: 'pointer',
}
